/**
 * Response Strategy Template Service
 * List/get is available to any account admin (powers the template picker);
 * create/update/delete are super-admin only, enforced server-side.
 */

import api from '@/lib/api';
import { withBuScope } from '@/lib/business-unit-scope';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse } from '@/types';
import type { ResponseStrategyTemplate, UpdateResponseStrategyTemplateDto } from '@/modules/response-strategies/types';

interface ResponseStrategyTemplatesListResponseDto {
	templates: ResponseStrategyTemplate[];
	count: number;
}

// The API only returns the Mongo `_id` field, not `id` — map it here the same
// way ResponseStrategyAdapter.fromApi does for regular strategies.
function withId(template: ResponseStrategyTemplate): ResponseStrategyTemplate {
	return { ...template, id: template.id ?? template._id ?? '' };
}

export const responseStrategyTemplateService = {
	async getTemplates(): Promise<ResponseStrategyTemplate[]> {
		const response = await api.get<ApiResponse<ResponseStrategyTemplatesListResponseDto>>(
			API_ENDPOINTS.RESPONSE_STRATEGY_TEMPLATES.BASE
		);
		return (response.data.data.templates ?? []).map(withId);
	},

	async getTemplateById(id: string): Promise<ResponseStrategyTemplate> {
		const response = await api.get<ApiResponse<ResponseStrategyTemplate>>(API_ENDPOINTS.RESPONSE_STRATEGY_TEMPLATES.BY_ID(id));
		return withId(response.data.data);
	},

	async updateTemplate(id: string, data: UpdateResponseStrategyTemplateDto): Promise<ResponseStrategyTemplate> {
		const response = await api.put<ApiResponse<ResponseStrategyTemplate>>(
			API_ENDPOINTS.RESPONSE_STRATEGY_TEMPLATES.BY_ID(id),
			data
		);
		return withId(response.data.data);
	},

	async deleteTemplate(id: string): Promise<void> {
		await api.delete<ApiResponse<void>>(API_ENDPOINTS.RESPONSE_STRATEGY_TEMPLATES.BY_ID(id));
	},

	// Intent mapping and enable/disable only need the regular edit permission,
	// unlike updateTemplate's content changes (name/prompt/strategy text),
	// which are super-admin only. Same path as BY_ID, PATCH instead of PUT.
	/**
	 * Despite the endpoint, this PATCH upserts a row into `response_strategies` — the
	 * account's override of a shared template — so it is a scoped WRITE and has to carry
	 * the brand. Without it the backend matched the override on (account, template) alone,
	 * and a second brand's toggle silently edited the FIRST brand's row.
	 */
	async updateTemplateEditableFields(
		id: string,
		data: { intents?: string[]; isActive?: boolean },
		scope: { bu_id?: string } = {}
	): Promise<ResponseStrategyTemplate> {
		const response = await api.patch<ApiResponse<ResponseStrategyTemplate>>(
			withBuScope(API_ENDPOINTS.RESPONSE_STRATEGY_TEMPLATES.BY_ID(id), scope),
			data
		);
		return withId(response.data.data);
	}
};
